/**
 * Communications React Query hooks.
 *
 * Reads via `directApiClient` through `client.ts` helpers. Mutations
 * (mark-read, promote-to-documents) follow the LF-3017 mutation pattern:
 * `mutationFn` calls the `client.ts` helper, `onSuccess` invalidates via
 * `getDependencies(...)` from `query-dependencies.ts` (single source of
 * truth — never hand-rolled).
 *
 * **The thread hooks are owner-scoped** — they take a `ThreadOwner` rather than
 * a bare `matterId`, so one set of hooks serves a matter thread and a lead
 * thread. `client.ts` routes each to the right endpoint. The remaining hooks
 * here stay matter-only by design: a lead has no conversation list (it owns
 * exactly one thread), no Documents tab to promote an attachment into, no
 * per-matter sidebar count, and no institution to export.
 */

'use client';

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query';
import { toast } from '@/lib/toast';
import { getOutlookSendBlocked } from './outlook-send-blocked';
import { directApiClient } from '@/lib/api/direct-client';
import { communicationsKeys, fileKeys, leadKeys } from '@/lib/query-keys';
import { getDependencies } from '@/lib/services/query-dependencies';
import { FRESH_DATA_CONFIG } from '../query-config';
import { matterOwner, threadOwnerId, type ThreadOwner } from './owner';
import {
  createInstitutionExport,
  discardAdHocDraft,
  getInstitutionExport,
  deleteImportedCorrespondence,
  getOutlookSearchAvailability,
  getThreadItems,
  importCorrespondenceIntoThread,
  importOutlookCorrespondenceIntoThread,
  importPmsCorrespondenceIntoThread,
  listConversations,
  markThreadRead,
  promoteAttachmentToDocuments,
  searchOutlookCorrespondence,
  uploadImportSourceFile,
  retryThreadSend,
  saveAdHocDraft,
  sendAdHocEmail
} from './client';
import type {
  ImportCorrespondenceResult,
  ListConversationsParams,
  OutlookCorrespondenceSearchResult,
  OutlookSearchAvailability,
  ParticipantType
} from './types';

// Baseline tick so newly-arrived inbound replies surface on the
// Communications tab without the user having to refresh. The window-focus
// refetch from FRESH_DATA_CONFIG only fires on tab/window changes — it
// misses the case where the user keeps the tab focused waiting for a
// reply.
const INBOUND_POLL_INTERVAL_MS = 30 * 1000;

/**
 * Conversation list for the Communications tab. Returns all threads in a
 * single response (not paginated). Safe to leave mounted — filter / sort /
 * search are query params, so each combination caches independently.
 */
export function useCommunicationThreads(
  matterId: number,
  params?: ListConversationsParams,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: communicationsKeys.threadList(matterOwner(matterId), params),
    queryFn: () => listConversations(matterId, params),
    enabled: options?.enabled ?? !!matterId,
    ...FRESH_DATA_CONFIG,
    refetchInterval: INBOUND_POLL_INTERVAL_MS,
    refetchIntervalInBackground: false
  });
}

/**
 * Thread items + header for a (`participantType`, `participantId`) pair on a
 * given owner. The composite participant key is required on a matter because
 * institution and contact IDs come from separate auto-increment columns and
 * bare-id collision is expected; on a lead it is the lead's own identity,
 * since a lead owns exactly one thread and is its own participant.
 */
export function useThreadItems(
  owner: ThreadOwner,
  participantType: ParticipantType | null,
  participantId: number | null,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey:
      participantType != null && participantId != null
        ? communicationsKeys.threadItems(owner, participantType, participantId)
        : communicationsKeys.threadItemsPending(owner),
    queryFn: () => {
      if (participantType == null || participantId == null) {
        throw new Error(
          'useThreadItems called without participantType/participantId'
        );
      }
      return getThreadItems(owner, participantType, participantId);
    },
    enabled:
      (options?.enabled ?? true) &&
      !!threadOwnerId(owner) &&
      participantType != null &&
      participantId != null,
    ...FRESH_DATA_CONFIG,
    refetchInterval: INBOUND_POLL_INTERVAL_MS,
    refetchIntervalInBackground: false
  });
}

/**
 * Mark a thread read. Idempotent; firm-wide effect. Invalidates only the
 * conversation list (unread pill recomputes) and the sidebar badge. Thread
 * items themselves don't change on mark-read — re-fetching them here triggers
 * a stampede when the mutation fires concurrently (Strict Mode, fast tab
 * switches), so we leave the items cache intact.
 *
 * The sidebar badge is matter-only: `['sidebar-counts', matterId]` is the
 * matter layout's per-matter count, and a lead has no such sidebar.
 *
 * **A lead's read receipt is visible outside the thread** (LF-6003), which the
 * matter's is not — so the lead branch invalidates the lead itself. Marking a
 * lead thread read stamps `threadLastSeenAt` / `threadLastSeenById`, and
 * `hasUnseenReply` is derived server-side from that instant against the last
 * inbound. That one derived boolean drives the drawer tab's blue dot *and* the
 * board row's unread marker *and* the board's ordering. Without this the
 * receipt lands in the database and nothing on screen changes: the coordinator
 * reads the reply and the row still says unread.
 */
export function useMarkThreadRead(owner: ThreadOwner) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      participantType,
      participantId
    }: {
      participantType: ParticipantType;
      participantId: number;
    }) => markThreadRead(owner, participantType, participantId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: communicationsKeys.threads(owner)
      });
      if (owner.type === 'matter') {
        queryClient.invalidateQueries({
          queryKey: ['sidebar-counts', owner.matterId]
        });
      } else {
        invalidateLeadDerivedState(queryClient, owner.leadId);
      }
    }
  });
}

/**
 * Refresh the surfaces that render a lead's **server-derived** state after a
 * thread write (LF-6003).
 *
 * Two keys, deliberately not `leadKeys.all`: the detail entry behind the open
 * drawer / `/leads/{id}` page, and every board list (`boards()` covers both the
 * in-progress board and the terminal pages). `leadKeys.all` would additionally
 * sweep the duplicate-check queries, which no thread write can affect.
 *
 * Invalidated rather than patched because **the frontend re-derives none of
 * this** — `hasUnseenReply`, `effectiveStage`, `daysInStage` and `isStalled` are
 * all resolved by `LeadStageService` from the thread's own timestamps, so the
 * server's answer is the only correct one.
 *
 * Direct `invalidateQueries` rather than a `query-dependencies.ts` entry: that
 * module is matter-only and types `matterId` as required, and both Scribe and
 * Intake set the precedent of invalidating their own keys directly
 * (`technical-spec.md` §18 trap 38).
 */
function invalidateLeadDerivedState(
  queryClient: ReturnType<typeof useQueryClient>,
  leadId: number
) {
  queryClient.invalidateQueries({ queryKey: leadKeys.detail(leadId) });
  queryClient.invalidateQueries({ queryKey: leadKeys.boards() });
}

/**
 * Send an ad-hoc email from a Communications thread (AD_HOC create + send).
 * Invalidates the thread items (the new outbound row + prior-outbound timer
 * change), the conversation list (lastActivity / awaiting-reply pills), and
 * the sidebar badge.
 */
export function useSendAdHocEmail(owner: ThreadOwner) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      correspondenceId?: number;
      participantType: ParticipantType;
      participantId: number;
      subject: string;
      body: string;
      followUpDays?: number | null;
      additionalRecipientContactMatterIds?: number[];
      excludeFirmSignature?: boolean;
      sendSenderCopy?: boolean;
      attachments?: number[];
      sourceEmailTemplateId?: number;
      aiDraftId?: number;
      parentCorrespondenceId?: number;
    }) => sendAdHocEmail(owner, payload),
    onSuccess: () => {
      // Sending an ad-hoc creates a new outbound row that lives on the
      // current thread (and on each fan-out sibling's thread, if any — a
      // matter-only case: a lead send is always single-recipient). The broad
      // owner-scoped invalidations cover both — narrower keys are not worth
      // the bookkeeping when the user just sent something and expects
      // everything to refresh anyway.
      queryClient.invalidateQueries({
        queryKey: communicationsKeys.threads(owner)
      });
      queryClient.invalidateQueries({
        queryKey: communicationsKeys.threadItemsForOwner(owner)
      });
      if (owner.type === 'matter') {
        queryClient.invalidateQueries({
          queryKey: ['sidebar-counts', owner.matterId]
        });
      } else {
        // A lead send moves `lastOutboundAt`, which `LeadStageService` reads
        // for `effectiveStage`, `daysInStage` and `isStalled` — so the row the
        // coordinator just emailed from is stale the moment the send lands
        // (LF-6003). Same reason as mark-read, a different derived field.
        invalidateLeadDerivedState(queryClient, owner.leadId);
      }
    }
  });
}

/**
 * Promote an inbound-email attachment to the matter's Documents tab.
 * Invalidates both the thread items (chip flips to "Filed to Documents")
 * and the documents/files lists.
 */
/**
 * Persist an AD_HOC draft (create or update). Lazy-create: the caller passes
 * `correspondenceId` only on subsequent saves of the same draft. Invalidates
 * the thread items (so `header.draft` refreshes), the conversation list (so
 * the `Draft` pill turns on), and the sidebar badge.
 */
export function useSaveAdHocDraft(owner: ThreadOwner) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      correspondenceId?: number;
      participantType: ParticipantType;
      participantId: number;
      subject: string;
      body: string;
      followUpDays?: number | null;
      additionalRecipientContactMatterIds?: number[];
      attachments?: number[];
      sourceEmailTemplateId?: number;
      aiDraftId?: number;
      parentCorrespondenceId?: number;
    }) => saveAdHocDraft(owner, payload),
    onSuccess: (_data, { participantType, participantId }) => {
      queryClient.invalidateQueries({
        queryKey: communicationsKeys.threads(owner)
      });
      queryClient.invalidateQueries({
        queryKey: communicationsKeys.threadItems(
          owner,
          participantType,
          participantId
        )
      });
    }
  });
}

/**
 * Discard the composer's draft (LF-6023). Invalidates the thread items (so
 * `header.draft` clears) and the conversation list (so the `Draft` pill turns off);
 * on a lead it also refreshes the enquiry's derived state, since dropping a draft
 * changes what the board's Draft signal reports.
 */
export function useDiscardAdHocDraft(owner: ThreadOwner) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (correspondenceId: number) =>
      discardAdHocDraft(owner, correspondenceId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: communicationsKeys.threads(owner)
      });
      queryClient.invalidateQueries({
        queryKey: communicationsKeys.threadItemsForOwner(owner)
      });
      if (owner.type === 'lead') {
        invalidateLeadDerivedState(queryClient, owner.leadId);
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to discard draft');
    }
  });
}

/**
 * Retry a send that terminally failed (LF-6023).
 *
 * Same invalidation set as a fresh send: the row leaves QUARANTINED, its failure
 * banner clears, and on a lead the send moves `lastOutboundAt`, which the board's
 * stage / stall derivation reads.
 */
export function useRetryThreadSend(owner: ThreadOwner) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (correspondenceId: number) =>
      retryThreadSend(owner, correspondenceId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: communicationsKeys.threads(owner)
      });
      queryClient.invalidateQueries({
        queryKey: communicationsKeys.threadItemsForOwner(owner)
      });
      if (owner.type === 'matter') {
        queryClient.invalidateQueries({
          queryKey: ['sidebar-counts', owner.matterId]
        });
      } else {
        invalidateLeadDerivedState(queryClient, owner.leadId);
      }
    },
    onError: (error: Error) => {
      // A retry on a matter whose Outlook identity isn't connected comes back
      // as a 409. The wire message is accurate but generic ("the Outlook
      // mailbox for this matter is not connected"); `reasonMessage` names WHO
      // to chase and WHERE, which is the difference between a dead end and an
      // action (VALE-332). It is the backend's own copy — do not rebuild it
      // from `reason` here (VALE-348).
      const blocked = getOutlookSendBlocked(error);
      if (blocked?.reasonMessage) {
        toast.error(blocked.reasonMessage);
        return;
      }
      toast.error(error.message || 'Failed to retry send');
    }
  });
}

/**
 * Light-touch thread mutations against the existing
 * `PUT /matters/:matterId/correspondences/:id` endpoint (D-12 / D-13). Covers:
 *
 *   - "Dismiss follow-up reminder" — `{ followUpCleared: true }` clears `follow_up_cleared_at`.
 *   - "Mark response received" — `{ responseReceived: true }` sets
 *     `response_received_at = now()`.
 *   - "Flag for follow-up" — `{ followUpDays: N }` on a SENT row recomputes
 *     `follow_up_due_at = now() + N days` server-side.
 *
 * One hook for all three so callers don't pick the wrong key; the FE never
 * combines these in a single PATCH, but the server doesn't care if they're
 * grouped or split. Invalidates thread items + conversation list + sidebar.
 */
export function useUpdateCorrespondence(matterId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      correspondenceId,
      ...payload
    }: {
      correspondenceId: number;
      followUpCleared?: boolean;
      responseReceived?: boolean;
      followUpDays?: number;
    }) =>
      directApiClient.put<unknown>(
        `/matters/${matterId}/correspondences/${correspondenceId}`,
        payload
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: communicationsKeys.threads(matterOwner(matterId))
      });
      queryClient.invalidateQueries({
        queryKey: communicationsKeys.threadItemsForOwner(matterOwner(matterId))
      });
      queryClient.invalidateQueries({
        queryKey: ['sidebar-counts', matterId]
      });
    }
  });
}

/**
 * Import correspondence that never flowed through Vale into this thread (VALE-597).
 *
 * One mutation covers the whole gesture — upload the bytes, then make a correspondence out
 * of them — because from the user's point of view it IS one gesture: they dropped a file on
 * a conversation. Splitting it would make the caller sequence two mutations and invent its
 * own in-between state for a step the user cannot see.
 *
 * The caller keeps a local pending card per in-flight file (keyed on a client id it
 * generates) and drops it when this settles; the real item arrives with the thread refetch.
 * That optimism is deliberately NOT a React Query cache write: the settled card's shape
 * depends on parsing the file server-side (a `.msg` from the firm's own address becomes an
 * outbound record, not an inbound email), so an optimistic item would have to guess the
 * kind and would guess wrong often enough to flicker.
 *
 * Matter-only: a lead thread has no import surface — its participant is the enquirer
 * themselves and there is no institution correspondence to file into it.
 */
export function useImportCorrespondence(
  matterId: number,
  participantType: ParticipantType,
  participantId: number
) {
  const queryClient = useQueryClient();
  return useMutation<
    ImportCorrespondenceResult,
    Error,
    { file: File; onProgress?: (percent: number) => void; signal?: AbortSignal }
  >({
    mutationFn: async ({ file, onProgress, signal }) => {
      const fileId = await uploadImportSourceFile(matterId, file, {
        onProgress,
        signal
      });
      return importCorrespondenceIntoThread(
        matterId,
        participantType,
        participantId,
        fileId
      );
    },
    onSuccess: (result) =>
      invalidateAfterImport(queryClient, matterOwner(matterId), result)
  });
}

/**
 * What the cache owes after an import lands, whichever door the bytes came through.
 *
 * Shared by the hand-upload import and the PMS one (VALE-1124) rather than written twice:
 * downstream of the landed file the two are the same operation, so a cache key added for
 * one and forgotten for the other is a stale thread that only reproduces on one of them.
 */
function invalidateAfterImport(
  queryClient: ReturnType<typeof useQueryClient>,
  owner: ThreadOwner,
  result: ImportCorrespondenceResult
) {
  // Invalidated on a duplicate too: nothing was created, but the row that already
  // existed may not be in this client's cache at all (imported from another session),
  // and the notice links to it.
  invalidateThreadAfterImportChange(queryClient, owner);
  if (result.outcome === 'imported' && owner.type === 'matter') {
    // An imported email's attachments land as documents on the correspondence, and its
    // analysis feeds the matter's AI review — both sit behind the Documents cascade.
    // An enquiry has neither (VALE-2014).
    getDependencies('document', owner.matterId).forEach((key) => {
      queryClient.invalidateQueries({ queryKey: key });
    });
  }
}

/** The thread's own caches, after an item is filed onto it or removed from it. */
function invalidateThreadAfterImportChange(
  queryClient: ReturnType<typeof useQueryClient>,
  owner: ThreadOwner
) {
  queryClient.invalidateQueries({
    queryKey: communicationsKeys.threadItemsForOwner(owner)
  });
  queryClient.invalidateQueries({
    queryKey: communicationsKeys.threads(owner)
  });
}

/**
 * Import a correspondence into this thread from the firm's PMS (VALE-1124).
 *
 * The sibling of {@link useImportCorrespondence} for the documents a firm files in
 * Smokeball or ActionStep instead of on the machine in front of it — which, on a surface
 * whose whole job is filing inbound correspondence, is where the inbound correspondence
 * actually lives.
 *
 * No progress channel, unlike the upload path: the bytes move PMS → Vale server-side, so
 * there is nothing for the browser to report a percentage of. The picker shows a busy
 * button and the same pending card covers the parse.
 */
export function useImportPmsCorrespondence(
  matterId: number,
  participantType: ParticipantType,
  participantId: number
) {
  const queryClient = useQueryClient();
  return useMutation<
    ImportCorrespondenceResult,
    Error,
    { externalId: string; fileName: string }
  >({
    mutationFn: (document) =>
      importPmsCorrespondenceIntoThread(
        matterId,
        participantType,
        participantId,
        document
      ),
    onSuccess: (result) =>
      invalidateAfterImport(queryClient, matterOwner(matterId), result)
  });
}

/**
 * Whether this viewer can search their connected Outlook mailbox from this thread
 * (VALE-1217), a matter's or an enquiry's (VALE-2014).
 *
 * Enabled lazily: it resolves a real mailbox connection server-side, so it runs when a
 * surface is about to offer the route, not on every thread render.
 */
export function useOutlookSearchAvailability(
  owner: ThreadOwner,
  participantType: ParticipantType,
  participantId: number,
  enabled: boolean
) {
  return useQuery<OutlookSearchAvailability>({
    queryKey: communicationsKeys.outlookSearchAvailability(
      owner,
      participantType,
      participantId
    ),
    queryFn: () =>
      getOutlookSearchAvailability(owner, participantType, participantId),
    enabled: enabled && threadOwnerId(owner) > 0 && participantId > 0,
    ...FRESH_DATA_CONFIG
  });
}

/**
 * Messages exchanged with this thread's participant, from the viewer's own mailbox
 * (VALE-1217).
 *
 * Paged rather than fetched whole: Graph caps a search at 1,000 results and admits no
 * `$orderby`, so pages arrive in the index's relevance order and the list grows as the
 * reader asks for more. The alternative — fetching every page up front to sort by date —
 * would read far more of someone's mailbox than the person is going to look at, which is
 * the opposite of what this feature promises.
 *
 * `gcTime: 0` is load-bearing, not a tuning knob. A page of search results is a slice of a
 * lawyer's mailbox, and the feature's whole claim is that nothing is retained for a message
 * that was looked at and not imported. Keeping it in the query cache after the dialog closes
 * would be exactly that retention, in the browser.
 */
export function useOutlookMessageSearch(
  owner: ThreadOwner,
  participantType: ParticipantType,
  participantId: number,
  enabled: boolean
) {
  return useInfiniteQuery<OutlookCorrespondenceSearchResult>({
    queryKey: communicationsKeys.outlookSearch(
      owner,
      participantType,
      participantId
    ),
    queryFn: ({ pageParam }) =>
      searchOutlookCorrespondence(
        owner,
        participantType,
        participantId,
        (pageParam as string | null) ?? null
      ),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: enabled && threadOwnerId(owner) > 0 && participantId > 0,
    gcTime: 0,
    staleTime: 0,
    refetchOnWindowFocus: false
  });
}

/**
 * Import one message picked out of the connected Outlook mailbox (VALE-1217).
 *
 * One message per call, exactly like the PMS door: a batch is the picker looping this, so a
 * refusal on one message reports on that message and the rest still land. The cache
 * contract is the shared one — downstream of the fetched bytes this IS the same import.
 */
export function useImportOutlookCorrespondence(
  owner: ThreadOwner,
  participantType: ParticipantType,
  participantId: number
) {
  const queryClient = useQueryClient();
  return useMutation<
    ImportCorrespondenceResult,
    Error,
    {
      graphMessageId: string;
      internetMessageId?: string | null;
      subject?: string | null;
    }
  >({
    mutationFn: (message) =>
      importOutlookCorrespondenceIntoThread(
        owner,
        participantType,
        participantId,
        message
      ),
    onSuccess: (result) => invalidateAfterImport(queryClient, owner, result)
  });
}

/**
 * Remove an imported correspondence from a thread (VALE-597).
 *
 * Offered only on imported items, and the backend refuses anything else — so a mis-drop is
 * undoable without the thread ever exposing a way to delete real correspondence history.
 */
export function useDeleteImportedCorrespondence(
  owner: ThreadOwner,
  participantType: ParticipantType,
  participantId: number
) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (correspondenceId) =>
      deleteImportedCorrespondence(
        owner,
        participantType,
        participantId,
        correspondenceId
      ),
    onSuccess: () => {
      invalidateThreadAfterImportChange(queryClient, owner);
      // The import's attachments went with it, so the Documents cascade is stale too.
      // An enquiry has no Documents cascade to refresh (VALE-2014).
      if (owner.type === 'matter') {
        getDependencies('document', owner.matterId).forEach((key) => {
          queryClient.invalidateQueries({ queryKey: key });
        });
      }
    }
  });
}

export function usePromoteAttachmentToDocuments(matterId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fileId: number) =>
      promoteAttachmentToDocuments(matterId, fileId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: communicationsKeys.threads(matterOwner(matterId))
      });
      // Thread items cache — every open thread may have stale chip state
      queryClient.invalidateQueries({
        queryKey: communicationsKeys.threadItemsForOwner(matterOwner(matterId))
      });
      // Documents tab list
      queryClient.invalidateQueries({ queryKey: fileKeys.forMatter(matterId) });
      // Cross-domain — `document` resolver carries the full Documents cascade
      getDependencies('document', matterId).forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key });
      });
    }
  });
}

/**
 * Download every correspondence (outbound + inbound), attachments, formal
 * letter PDFs, email-body PDFs and file notes for a single (matter,
 * institution) thread as one ZIP. The backend is async: this hook kicks off
 * the export, polls every 2s up to ~60s, then triggers a browser download
 * against the presigned URL.
 *
 * Not a `useMutation` because the work is a multi-step orchestration with a
 * side-effect at the end (anchor click) — wrapping it in a mutation would
 * obscure the polling without adding cache benefits.
 */
const EXPORT_POLL_INTERVAL_MS = 2000;
const EXPORT_POLL_TIMEOUT_MS = 60_000;

export function useDownloadInstitutionCorrespondence(matterId: number) {
  return useMutation({
    mutationFn: async (matterInstitutionId: number): Promise<void> => {
      const created = await createInstitutionExport(
        matterId,
        matterInstitutionId
      );
      const deadline = Date.now() + EXPORT_POLL_TIMEOUT_MS;
      while (Date.now() < deadline) {
        await new Promise((resolve) =>
          setTimeout(resolve, EXPORT_POLL_INTERVAL_MS)
        );
        const status = await getInstitutionExport(
          matterId,
          matterInstitutionId,
          created.id
        );
        if (status.status === 'completed') {
          if (!status.downloadUrl) {
            throw new Error(
              'Export completed but no download URL was returned'
            );
          }
          // Hidden-anchor click — keeps the user on the page and lets the
          // browser respect the S3 Content-Disposition header for filename.
          const a = document.createElement('a');
          a.href = status.downloadUrl;
          a.rel = 'noopener';
          document.body.appendChild(a);
          a.click();
          a.remove();
          return;
        }
        if (status.status === 'failed') {
          throw new Error(status.errorMessage ?? 'Export failed');
        }
      }
      throw new Error(
        'Export is taking longer than expected. Please try again in a moment.'
      );
    }
  });
}
